import { Course } from '../types';

/**
 * The minimum a bundle has to be for the shelf rule to reason about it.
 * Structural on purpose: the authenticated `Bundle` and the anon-facing
 * `PublicBundle` are different shapes, and one rule has to govern both or the
 * signed-in and logged-out shelves drift apart.
 */
export interface ShelfBundle {
  published: boolean;
  courses: Array<{ id: string }>;
}

/**
 * The shelf rule: a course that belongs to a published bundle is shown *inside*
 * that bundle rather than alongside it as a loose card.
 *
 * OPT-IN, and OFF by default (Aug 2026). It shipped as an unconditional rule on
 * the argument that nothing should appear twice at the same altitude. In
 * practice the dedup costs more than the duplication did: a course silently
 * leaves the catalogue the moment someone publishes a bundle containing it,
 * which reads as a bug from every side — the author can't find their course on
 * the shelf, and a student who wants that one topic can't reach it without
 * going through a pack they may not want to buy. A bundle is a discount, not a
 * replacement for the thing it contains.
 *
 * So it is now `bundle_shelf_dedupe`, off unless a tenant asks for it. Callers
 * pass `enabled` explicitly — there is no default — because the failure mode is
 * a call site quietly getting the wrong one.
 *
 * Scope when it IS on, which is easy to get wrong:
 *
 * - **Published bundles only.** A draft bundle isn't on the shelf, so its
 *   members aren't duplicated and must keep their own cards.
 * - **Student shelves only.** `CourseContext.courses` is also the instructor
 *   and admin list (`getInstructorCourses`), so this must never be applied
 *   there — it would hide bundled courses from the people who build them.
 * - **Only where the bundle is actually on screen.** The rule exists to stop
 *   one product appearing twice, so it is suspended wherever the bundles
 *   section is hidden — under a tag filter on `/catalog`, and under any
 *   search/category/tag filter on the public catalogue. Hiding members while
 *   their bundle is also hidden would make them unreachable through the very
 *   filter someone is using to find them.
 *
 * Applies on all three student shelves plus the public marketing catalogue,
 * which reads through `publicService` and is anon-visible (20260809000002).
 */

/** Course ids that live inside at least one published bundle. */
export function bundledCourseIdsOnShelf(bundles: ShelfBundle[]): Set<string> {
  const ids = new Set<string>();
  for (const bundle of bundles) {
    if (!bundle.published) continue;
    for (const course of bundle.courses) ids.add(course.id);
  }
  return ids;
}

/**
 * Apply the shelf rule to a student-facing course list.
 *
 * `enabled` is the tenant's `bundle_shelf_dedupe` flag and is required — see
 * the note above. Returns the input array untouched (same reference) when the
 * rule is off or nothing is bundled, so the common case costs nothing.
 */
export function applyShelfRule<T extends Pick<Course, 'id'>>(
  courses: T[],
  publishedBundles: ShelfBundle[],
  enabled: boolean,
): T[] {
  if (!enabled) return courses;
  const hidden = bundledCourseIdsOnShelf(publishedBundles);
  if (hidden.size === 0) return courses;
  return courses.filter((c) => !hidden.has(c.id));
}
